import { Box, Stack, Typography } from '@mui/material';
import NumberInput from '~/components/Inputs/NumberInput';
import { mmToCm } from '~/utils/units';
import { MAX_MARGIN_MM, marginStepCm } from './marginRange';

/** A margin per patient direction, in cm. */
export type AsymmetricMargins = {
    left: number;
    right: number;
    anterior: number;
    posterior: number;
    superior: number;
    inferior: number;
};

const LABELS: Record<keyof AsymmetricMargins, string> = {
    left: 'Left',
    right: 'Right',
    anterior: 'Anterior',
    posterior: 'Posterior',
    superior: 'Superior',
    inferior: 'Inferior',
};

/** The fields as laid out: one row per axis, both ends side by side. */
const ROWS: [keyof AsymmetricMargins, keyof AsymmetricMargins][] = [
    ['right', 'left'],
    ['posterior', 'anterior'],
    ['superior', 'inferior'],
];

interface AsymmetricMarginControlsProps {
    margins: AsymmetricMargins;
    onMarginChange: (
        direction: keyof AsymmetricMargins,
        valueCm: number
    ) => void;
    /** See {@link NumberInput}'s `onValidityChange`; reported per field. */
    onValidityChange: (
        direction: keyof AsymmetricMargins,
        isValid: boolean
    ) => void;
}

const inputStyle = {
    textAlign: 'center' as const,
    width: '50px',
    fontSize: 13,
    paddingTop: 5,
    paddingBottom: 4,
};

export default function AsymmetricMarginControls({
    margins,
    onMarginChange,
    onValidityChange,
}: AsymmetricMarginControlsProps) {
    return (
        <Stack spacing={0.75}>
            <Typography fontSize={11} color="text.secondary" textAlign="right">
                Values in cm
            </Typography>
            {ROWS.map((row) => (
                <Stack key={row[0]} direction="row" spacing={2}>
                    {row.map((direction) => (
                        <MarginInput
                            key={direction}
                            label={LABELS[direction]}
                            value={margins[direction]}
                            onChange={(value) =>
                                onMarginChange(direction, value)
                            }
                            onValidityChange={(isValid) =>
                                onValidityChange(direction, isValid)
                            }
                        />
                    ))}
                </Stack>
            ))}
        </Stack>
    );
}

interface MarginInputProps {
    label: string;
    value: number;
    onChange: (value: number) => void;
    onValidityChange: (isValid: boolean) => void;
}

function MarginInput({
    label,
    value,
    onChange,
    onValidityChange,
}: MarginInputProps) {
    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
            }}
        >
            <Typography
                fontSize={12}
                color="text.secondary"
                sx={{ width: 60, flexShrink: 0 }}
            >
                {label}
            </Typography>
            <NumberInput
                value={value}
                onChange={(v) => onChange(v ?? 0)}
                onValidityChange={onValidityChange}
                min={0}
                max={mmToCm(MAX_MARGIN_MM)}
                step={marginStepCm}
                snapToStep
                precision={2}
                inputProps={{ style: inputStyle, 'aria-label': label }}
                commitOnChange
                tooltipPlacement="left"
            />
        </Box>
    );
}
